import type { Protection } from '../collectors/privacy-signals';
import type { Signal } from '../collectors/types';
import { prop } from '../collectors/util';

export type BrowserFamily = 'uniform' | 'firefox' | 'brave' | 'edge' | 'chrome' | 'chromium' | 'safari' | 'other';

export interface BrowserInfo {
  family: BrowserFamily;
  name: string;
  mobile: boolean;
  ios: boolean;
}

type SignalMap = ReadonlyMap<string, Signal>;

export function detectBrowser(m: SignalMap, uniformMode: boolean): BrowserInfo {
  const ua = String(m.get('nav.userAgent')?.value ?? '');
  const touch = Number(m.get('hw.touch')?.value ?? 0);
  const ios = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && touch > 1);
  const mobile = ios || /Mobi|Android/.test(ua) || prop<boolean>(m.get('nav.uaData')?.value, 'mobile') === true;
  const brave = prop<boolean>(m.get('nav.vendorGlobals')?.value, 'brave') === true;

  const pick = (family: BrowserFamily, name: string): BrowserInfo => ({ family, name, mobile, ios });
  if (uniformMode) return pick('uniform', 'Tor Browser or Firefox with resistFingerprinting');
  if (brave) return pick('brave', 'Brave');
  if (ios && !/Version\/[\d.]+ (Mobile\/\S+ )?Safari/.test(ua)) return pick('safari', 'an iOS browser (Safari engine)');
  if (/Firefox\/|FxiOS/.test(ua)) return pick('firefox', 'Firefox');
  if (/Edg(A|iOS)?\//.test(ua)) return pick('edge', 'Microsoft Edge');
  if (/OPR\/|Vivaldi|YaBrowser|SamsungBrowser/.test(ua)) return pick('chromium', 'a Chromium-based browser');
  if (/Chrome\/|CriOS/.test(ua)) return pick('chrome', 'Google Chrome');
  if (/Safari\//.test(ua) && /Version\//.test(ua)) return pick('safari', 'Safari');
  return pick('other', 'your browser');
}

export type Priority = 'high' | 'medium' | 'low';

export interface Tip {
  id: string;
  priority: Priority;
  title: string;
  body: string;
  steps?: string[];
}

const has = (protections: Protection[], id: string) => protections.some((p) => p.id === id || p.id.startsWith(`${id}.`));

function browserTip(b: BrowserInfo, protections: Protection[]): Tip {
  const canvasProtected = has(protections, 'canvas.randomization') || has(protections, 'volatile');
  switch (b.family) {
    case 'uniform':
      return {
        id: 'browser',
        priority: 'high',
        title: 'You already use the strongest approach',
        body: 'Tor Browser and resistFingerprinting make many values identical for every user of that mode. The protection depends on not standing out from that crowd.',
        steps: [
          'Keep the window at its default size (letterboxing handles resizing).',
          'Do not install extra extensions or change fingerprint-related settings.',
          'Keep the browser updated so you match other users of the same version.',
        ],
      };
    case 'firefox':
      return {
        id: 'browser',
        priority: 'high',
        title: 'Turn on Firefox’s fingerprinting protections',
        body: canvasProtected
          ? 'Canvas protection is already active. resistFingerprinting goes further by standardizing many more values.'
          : 'Firefox ships strong protections, but the strongest are off by default.',
        steps: [
          'Settings → Privacy & Security → Enhanced Tracking Protection → Strict. This adds per-session canvas noise and blocks known fingerprinting scripts.',
          'Strongest: about:config → privacy.resistFingerprinting = true. Side effects: UTC time zone, a light theme for sites, English page requests, and some sites break.',
          'With resistFingerprinting on, also set privacy.resistFingerprinting.letterboxing = true to round your window size.',
        ],
      };
    case 'brave':
      return {
        id: 'browser',
        priority: 'high',
        title: 'Keep Brave Shields’ fingerprinting protection on',
        body: 'Brave adds per-site, per-session noise to canvas, WebGL, audio and several navigator values, so trackers cannot link you across sites or sessions.',
        steps: [
          'Click the Brave (lion) icon → make sure fingerprinting protection is on for each site. Set the default at brave://settings/shields.',
          'If your version offers a stricter fingerprinting level, it hides more at the cost of breaking some sites.',
          'brave://settings/privacy → WebRTC IP handling policy → “Disable non-proxied UDP”.',
        ],
      };
    case 'safari':
      return {
        id: 'browser',
        priority: 'high',
        title: 'Turn on Advanced Fingerprinting Protection for all browsing',
        body: 'Safari already shows pages only system fonts, a generic GPU name and hidden local IPs. Advanced protection adds noise to canvas and other APIs, but it is on by default only in Private Browsing.',
        steps: b.ios || b.mobile
          ? ['Settings → Apps → Safari → Advanced → Advanced Tracking and Fingerprinting Protection → All Browsing.', 'On iOS every browser uses Safari’s engine, so this is the main setting available to you.']
          : ['Safari → Settings → Advanced → “Use advanced tracking and fingerprinting protection” → in all browsing.'],
      };
    case 'chrome':
    case 'edge':
    case 'chromium':
      return {
        id: 'browser',
        priority: 'high',
        title: `${b.family === 'edge' ? 'Edge' : 'Chrome'} has few built-in fingerprinting defences`,
        body: 'Chromium exposes your full GPU name, detailed Client Hints, and unmodified canvas, WebGL and audio output.',
        steps: [
          ...(b.family === 'edge' ? ['Settings → Privacy, search, and services → Tracking prevention → Strict.'] : []),
          'Install one reputable content blocker (for example uBlock Origin Lite) to stop known fingerprinting scripts before they run.',
          'Canvas- and WebGL-spoofing extensions exist, but sites can detect them, and they often make you more unique. Prefer switching browsers if fingerprinting matters to you.',
          'For built-in protection, use Brave (Chromium-based, randomizes by default) or Firefox with Strict protection.',
        ],
      };
    default:
      return {
        id: 'browser',
        priority: 'high',
        title: 'Use a browser with built-in fingerprinting protection',
        body: 'Brave, Firefox (Strict or resistFingerprinting), Safari (Advanced Fingerprinting Protection) and Tor Browser all neutralize the strongest signals.',
      };
  }
}

export function buildTips(m: SignalMap, browser: BrowserInfo, protections: Protection[]): Tip[] {
  const tips: Tip[] = [];

  const exposure = prop<string>(m.get('webrtc.localIps')?.value, 'exposure');
  if (exposure === 'public' || exposure === 'private') {
    const steps: Record<string, string> = {
      firefox: 'about:config → media.peerconnection.ice.default_address_only = true (or media.peerconnection.enabled = false to turn WebRTC off).',
      brave: 'brave://settings/privacy → WebRTC IP handling policy → “Disable non-proxied UDP”.',
      chrome: 'Install Google’s official “WebRTC Network Limiter” extension.',
      edge: 'Install the “WebRTC Network Limiter” extension, or use Brave or Firefox.',
      chromium: 'Look for a WebRTC IP handling setting, or install “WebRTC Network Limiter”.',
    };
    tips.push({
      id: 'webrtc',
      priority: 'high',
      title: 'Stop WebRTC from revealing your IP address',
      body: `This page saw a ${exposure === 'public' ? 'public' : 'real local'} IP address through WebRTC without any permission. A tracker using a STUN server would also learn your public IP, even behind some VPNs.`,
      steps: [steps[browser.family] ?? 'Check your browser’s WebRTC privacy settings, or switch to a browser that hides local addresses by default.'],
    });
  }

  tips.push(browserTip(browser, protections));

  const extras = prop<number>(m.get('fonts.osGuess')?.value, 'extras') ?? 0;
  if (extras >= 8) {
    tips.push({
      id: 'fonts',
      priority: 'medium',
      title: 'Remove fonts you do not use',
      body: `${extras} fonts beyond your OS defaults are visible to every page. Fonts installed by Office, Adobe Creative Cloud, design tools or language packs make your list distinctive.`,
      steps: ['Uninstall font packs you no longer need.', 'Or use a browser that exposes only system fonts (Safari, Tor Browser, Firefox with resistFingerprinting).'],
    });
  }

  const langs = m.get('locale.languages')?.value;
  if (Array.isArray(langs) && langs.length >= 3) {
    tips.push({
      id: 'languages',
      priority: 'medium',
      title: 'Trim your language list',
      body: `Your browser announces ${langs.length} languages to every site. Each extra language narrows the crowd you blend into.`,
      steps: ['Keep only the languages you actually read in your browser’s language settings.'],
    });
  }

  if (prop<string>(m.get('locale.tzMatch')?.value, 'result') === 'mismatch') {
    tips.push({
      id: 'vpn-timezone',
      priority: 'low',
      title: 'A VPN does not hide your time zone',
      body: 'Your time zone and language point to different continents. If you rely on a VPN to hide your location, sites can still read your system time zone.',
    });
  }

  tips.push(
    {
      id: 'window',
      priority: 'low',
      title: 'Avoid unusual window sizes',
      body: 'Odd window and viewport sizes are distinctive within a session. Keep your browser maximized, or let Firefox letterboxing round it.',
    },
    {
      id: 'extensions',
      priority: 'low',
      title: 'Do not stack extensions',
      body: 'Each extension that changes pages can be detected, and an unusual combination is itself a fingerprint. One well-known content blocker beats five niche privacy add-ons.',
    },
    {
      id: 'updates',
      priority: 'low',
      title: 'Stay on the current browser version',
      body: 'Most people auto-update. An old version stands out in the user agent, the Client Hints and the engine quirks.',
    },
  );

  if (browser.family !== 'uniform') {
    tips.push({
      id: 'tor',
      priority: 'low',
      title: 'The reference approach: Tor Browser',
      body: 'Tor Browser’s goal is a uniform fingerprint: every user reports the same values, uses letterboxed windows and runs no extra extensions. If you need strong anonymity, use it without customizing.',
    });
  }

  tips.push({
    id: 'cookies',
    priority: 'low',
    title: 'Fingerprinting is one technique among several',
    body: 'Blocking third-party cookies, clearing site data and logging out of big platforms still matter. A fingerprint mostly helps trackers that cannot set cookies.',
  });

  return tips;
}
